import { Injectable } from '@angular/core';
import { NumberLocalizationService } from 'shared-components';

import { QuestionAnswerDataType } from 'app/student/constants/question-answer-data-type';

@Injectable({
  providedIn: 'root'
})
export class ValidationService {
  // Validation for e-mails. The regex here is taken from MDN.
  public static isValidEmail(email: string): boolean {
    const emailValidationRegex = /^[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/i;

    return emailValidationRegex.test(email);
  }

  // Validation for URLs
  public static isValidUrl(url: string): boolean {
    const urlValidationRegex = /^https?:\/\/(www\.)?[-a-zA-Z0-9@:%._+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_+.~#?&//=]*)$/i;

    return urlValidationRegex.test(url);
  }

  // Validation for phone numbers
  public static isValidPhoneNumber(phone: string): boolean {
    const phoneValidationRegex = /^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/im;

    return phoneValidationRegex.test(phone);
  }

  // Password validation, for reset password, account creation, etc.

  public static isValidPasswordLength(password: string): boolean {
    return password.length >= 4;
  }

  // More checks may go here in the future, but for now it's very simple.
  public static isValidPassword(password: string): boolean {
    return this.isValidPasswordLength(password);
  }

  // Validation for usernames
  public static isValidUsername(name: string): boolean {
    const unameRegex = /^[a-z0-9_]{3,28}$/i;

    return unameRegex.test(name);
  }

  public static isAlphaNum(text: string): boolean {
    const alphaNumRegex = /^[a-zA-Z0-9]*$/;

    return alphaNumRegex.test(text);
  }

  public static isValidAnswer(
    answerText: string, answerDataType: QuestionAnswerDataType, answerLocale?: string, answerNumberFormatLocale?: string): boolean {
    // Failsafe if answerNumberFormatLocale is not provided, to default to answerLocale
    const effectiveLocale = answerNumberFormatLocale && answerNumberFormatLocale.trim() !== '' ? answerNumberFormatLocale : answerLocale;

    if (!answerText) {
      return false;
    }

    if (answerDataType === QuestionAnswerDataType.Text) {
      return true;
    }

    if (answerDataType === QuestionAnswerDataType.Integer) {
      return NumberLocalizationService.isInteger(answerText, effectiveLocale);
    }

    const parsedNumber = NumberLocalizationService.parse(answerText, effectiveLocale);

    return !Number.isNaN(parsedNumber);
  }

  public static restrictAndSanitizeInput(
    value: string,
    answerDataType: QuestionAnswerDataType,
    locale = 'en-CA'
  ): string {
    const decimalSeparator = NumberLocalizationService.getDecimalSeparator(locale);

    let sanitized = value.trim();

    switch (answerDataType) {
      case QuestionAnswerDataType.Integer:
        sanitized = sanitized.replace(/[^0-9-]/g, '');
        sanitized = sanitized.replace(/(?!^)-/g, '');
        if (sanitized.indexOf('-') > 0) { sanitized = sanitized.replace('-', ''); }
        return sanitized;

      case QuestionAnswerDataType.Decimal:
        sanitized = sanitized.replace(/[^0-9.,-]/g, '');
        sanitized = sanitized.replace(/[.,]/g, decimalSeparator);
        sanitized = sanitized.replace(/(?!^)-/g, '');
        if (sanitized.indexOf('-') > 0) { sanitized = sanitized.replace('-', ''); }
        const parts = sanitized.split(decimalSeparator);
        if (parts.length > 2) { sanitized = parts[0] + decimalSeparator + parts.slice(1).join(''); }
        return sanitized;

      default:
        return sanitized;
    }
  }
}
